import {
  LAB_COMPARATORS,
  LAB_INTERPRETATIONS,
  LAB_OPTION_INTERPRETATIONS,
  LAB_PRIORITIES,
  LAB_REPORT_STATUSES,
  LAB_RESULT_TYPES,
} from '@wodi/domain';
import { z } from 'zod';

import { actorSchema, ageSchema, codedValueSchema, reasonSchema } from './clinical-common.ts';
import {
  cultureSchema,
  patientPreliminaryReportSchema,
  preliminaryCultureReportSchema,
} from './lab-micro.ts';
import { qcHoldSchema } from './lab-qc.ts';
import { sexSchema } from './patients.ts';
import { shaApprovalSchema } from './sha-approval.ts';

// The lab API (docs/plans/phase1-lab.md §3, §4). Result values cross as decimal strings
// typed by a person and are parsed by @wodi/domain on both sides; the server's
// interpretation is the one stored. Clinical notes, values and comments travel only in
// bodies, never in URLs or query strings.

// --- Catalogue ------------------------------------------------------------------------

const nameSchema = z.string().trim().min(1).max(120);
const ACTIVE_STATUSES = ['active', 'inactive'] as const;

export const labSectionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  sortOrder: z.number().int(),
  status: z.enum(ACTIVE_STATUSES),
});
export type LabSectionView = z.infer<typeof labSectionSchema>;

export const labSectionInputSchema = z.object({
  name: nameSchema,
  sortOrder: z.number().int().min(0).max(999).default(0),
});

export const updateLabSectionSchema = z
  .object({
    name: nameSchema,
    sortOrder: z.number().int().min(0).max(999),
    status: z.enum(ACTIVE_STATUSES),
  })
  .partial();

/** The tenant-local value sets the lab keeps (plan §2.1, C12). */
export const LAB_VALUE_SETS = [
  'lab_specimen_type',
  'lab_container',
  'lab_rejection_reason',
] as const;
export type LabValueSet = (typeof LAB_VALUE_SETS)[number];

export const labValueSetConceptSchema = z.object({
  code: z.string(),
  display: z.string(),
  status: z.enum(ACTIVE_STATUSES),
  sortOrder: z.number().int(),
  /** Always true for now: no national list is loaded (S2). */
  provisional: z.boolean(),
});
export type LabValueSetConceptView = z.infer<typeof labValueSetConceptSchema>;

export const labValueSetInputSchema = z.object({
  display: nameSchema,
  sortOrder: z.number().int().min(0).max(999).default(0),
});

export const updateLabValueSetConceptSchema = z
  .object({
    display: nameSchema,
    sortOrder: z.number().int().min(0).max(999),
    status: z.enum(ACTIVE_STATUSES),
  })
  .partial();

export const labAnalyteOptionSchema = z.object({
  code: z.string(),
  display: z.string(),
  interpretation: z.enum(LAB_OPTION_INTERPRETATIONS),
  sortOrder: z.number().int(),
  status: z.enum(ACTIVE_STATUSES),
});
export type LabAnalyteOptionView = z.infer<typeof labAnalyteOptionSchema>;

export const labUnitSchema = z.object({
  /** UCUM, checked with checkUcumCode. */
  code: z.string().trim().min(1).max(40),
  display: z.string().trim().min(1).max(40),
});

/** A LOINC code picked from the active version in the terminology cache. */
export const labLoincRefSchema = z.object({
  system: z.string().trim().min(1).max(200),
  version: z.string().trim().min(1).max(64),
  code: z.string().trim().min(1).max(64),
});

export const labAnalyteSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  resultType: z.enum(LAB_RESULT_TYPES),
  unit: labUnitSchema.nullable(),
  decimals: z.number().int().nullable(),
  allowsNegative: z.boolean(),
  loinc: codedValueSchema.extend({ version: z.string() }).nullable(),
  options: z.array(labAnalyteOptionSchema),
  status: z.enum(ACTIVE_STATUSES),
  statusReason: z.string().nullable(),
  version: z.number().int(),
  /** Whether any result exists, which fixes the result type and unit. */
  inUse: z.boolean(),
});
export type LabAnalyteView = z.infer<typeof labAnalyteSchema>;

export const labAnalyteInputSchema = z.object({
  name: nameSchema,
  shortName: z.string().trim().min(1).max(20),
  resultType: z.enum(LAB_RESULT_TYPES),
  unit: labUnitSchema.nullable().default(null),
  decimals: z.number().int().min(0).max(4).nullable().default(null),
  allowsNegative: z.boolean().default(false),
  loinc: labLoincRefSchema.nullable().default(null),
  /** Coded results only: the answers, in order. */
  options: z.array(nameSchema).max(30).default([]),
});

export const updateLabAnalyteSchema = z.object({
  expectedVersion: z.number().int().min(1),
  name: nameSchema.optional(),
  shortName: z.string().trim().min(1).max(20).optional(),
  unit: labUnitSchema.optional(),
  decimals: z.number().int().min(0).max(4).optional(),
  allowsNegative: z.boolean().optional(),
  loinc: labLoincRefSchema.nullable().optional(),
  status: z.enum(ACTIVE_STATUSES).optional(),
  statusReason: reasonSchema.optional(),
});

/** Adds answers and switches them on or off; answers are never deleted. */
export const putLabAnalyteOptionsSchema = z.object({
  expectedVersion: z.number().int().min(1),
  options: z
    .array(
      z.object({
        code: z.string().optional(),
        display: nameSchema,
        status: z.enum(ACTIVE_STATUSES).default('active'),
      }),
    )
    .min(1)
    .max(30),
});

export const labComponentSchema = z.object({
  analyteId: z.uuid(),
  analyteName: z.string(),
  shortName: z.string(),
  resultType: z.enum(LAB_RESULT_TYPES),
  unit: labUnitSchema.nullable(),
  sortOrder: z.number().int(),
  heading: z.string().nullable(),
  required: z.boolean(),
});
export type LabComponentView = z.infer<typeof labComponentSchema>;

const codeDisplaySchema = z.object({ code: z.string(), display: z.string() });

/** Culture tests carry the culture record (lab QC and microbiology plan §1.2). */
export const LAB_TEST_WORKFLOWS = ['standard', 'culture'] as const;
export type LabTestWorkflow = (typeof LAB_TEST_WORKFLOWS)[number];

export const labTestDefinitionSchema = z.object({
  id: z.uuid(),
  section: z.object({ id: z.uuid(), name: z.string() }),
  specimenType: codeDisplaySchema,
  container: codeDisplaySchema,
  reportNote: z.string().nullable(),
  turnaroundTargetMinutes: z.number().int().nullable(),
  setupState: z.enum(['draft', 'ready']),
  workflow: z.enum(LAB_TEST_WORKFLOWS),
  version: z.number().int(),
  components: z.array(labComponentSchema),
});

export const labTestSchema = z.object({
  serviceItemId: z.uuid(),
  code: z.string(),
  name: z.string(),
  itemStatus: z.enum(ACTIVE_STATUSES),
  /** Null: priced in the catalogue but "Not set up for the lab". */
  definition: labTestDefinitionSchema.nullable(),
});
export type LabTestView = z.infer<typeof labTestSchema>;

export const putLabTestDefinitionSchema = z.object({
  /** Null when defining the test for the first time. */
  expectedVersion: z.number().int().min(1).nullable(),
  sectionId: z.uuid(),
  specimenTypeCode: z.string().min(1).max(64),
  containerCode: z.string().min(1).max(64),
  reportNote: z.string().trim().max(500).nullable().default(null),
  turnaroundTargetMinutes: z.number().int().min(1).max(20_160).nullable().default(null),
  /** In report order. */
  components: z
    .array(
      z.object({
        analyteId: z.uuid(),
        heading: z.string().trim().min(1).max(60).nullable().default(null),
        required: z.boolean().default(true),
      }),
    )
    .max(60),
  /** Ready tests can be ordered; ready needs at least one component, unless a culture. */
  ready: z.boolean(),
  workflow: z.enum(LAB_TEST_WORKFLOWS).default('standard'),
});

export const labSettingsSchema = z.object({
  validationStages: z.enum(['one', 'two']),
  validatorMustDiffer: z.boolean(),
  useReceiveStep: z.boolean(),
  labelCopies: z.number().int().min(1).max(5),
  reportPaper: z.enum(['a4', 'a5']),
  /** C6: empty until the clinical lead sets it; escalation is immediate until then. */
  criticalEscalationMinutes: z.number().int().min(1).max(1440).nullable(),
});
export type LabSettingsView = z.infer<typeof labSettingsSchema>;

// --- Orders (plan §4) -------------------------------------------------------------------

export const LAB_ORDER_SOURCES = ['consultation', 'walk_in', 'external_request'] as const;

/** The order contract the consultation slice calls (plan §4.1). */
export const labOrderRequestSchema = z
  .object({
    clientRequestId: z.uuid(),
    source: z.enum(LAB_ORDER_SOURCES),
    /** Required unless an external request. */
    orderingUserId: z.uuid().nullable(),
    /** Name and facility of an outside clinician; external requests only. */
    externalRequester: z.string().trim().min(3).max(200).nullable().default(null),
    /** Clinical time; default now. */
    orderedAt: z.iso.datetime().optional(),
    tests: z
      .array(
        z.object({
          serviceItemId: z.uuid(),
          priority: z.enum(LAB_PRIORITIES).default('routine'),
          clinicalNotes: z.string().trim().max(500).nullable().default(null),
        }),
      )
      .min(1)
      .max(30),
  })
  .refine(
    (r) => (r.source === 'external_request') === (r.orderingUserId === null),
    'Name the ordering clinician, or the outside requester for an external request',
  )
  .refine(
    (r) => (r.source === 'external_request') === (r.externalRequester !== null),
    'An external request names the outside clinician and their facility',
  );
export type LabOrderRequest = z.input<typeof labOrderRequestSchema>;

/**
 * Whether a sample can be collected yet (plan §4.4), in the same words everywhere:
 * reception, the lab and the clinician.
 */
export const LAB_GATE_STATES = [
  'ready',
  'waiting_for_payment',
  'not_priced',
  'waiting_for_approval',
  'approval_declined',
] as const;
export type LabGateState = (typeof LAB_GATE_STATES)[number];

export const LAB_GATE_LABELS: Record<LabGateState, string> = {
  ready: 'Ready to collect',
  waiting_for_payment: 'Waiting for payment',
  not_priced: 'Not priced: ask the cashier',
  waiting_for_approval: 'Waiting for approval',
  approval_declined: 'Approval declined',
};

export const labOrderSchema = z.object({
  id: z.uuid(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  serviceItemId: z.uuid(),
  testName: z.string(),
  sectionName: z.string(),
  priority: z.enum(LAB_PRIORITIES),
  source: z.enum(LAB_ORDER_SOURCES),
  orderingUser: actorSchema.nullable(),
  externalRequester: z.string().nullable(),
  orderedAt: z.iso.datetime(),
  status: z.enum(['active', 'completed', 'cancelled', 'entered_in_error']),
  statusReason: z.string().nullable(),
  gate: z.enum(LAB_GATE_STATES),
  sample: z.object({ id: z.uuid(), sampleNumber: z.string(), status: z.string() }).nullable(),
  report: z
    .object({ id: z.uuid(), version: z.number().int(), status: z.enum(LAB_REPORT_STATUSES) })
    .nullable(),
});
export type LabOrderView = z.infer<typeof labOrderSchema>;

export const placeLabOrdersResultSchema = z.object({
  orders: z.array(labOrderSchema),
  /** Passive: the same test is already requested on this visit (C16). */
  warnings: z.array(z.object({ serviceItemId: z.uuid(), message: z.string() })),
});

export const cancelLabOrderSchema = z.object({ reason: reasonSchema });

export const labTestSearchRequestSchema = z.object({
  query: z.string().trim().min(2).max(100),
  visitId: z.uuid(),
});

export const labTestSearchResultSchema = z.object({
  items: z.array(
    z.object({
      serviceItemId: z.uuid(),
      code: z.string(),
      name: z.string(),
      sectionName: z.string(),
      specimenType: z.string(),
      /** Price for this visit's payer, or why there isn't one. */
      price: z.union([
        z.object({ state: z.literal('priced'), unitPriceCents: z.string() }),
        z.object({ state: z.literal('not_priced'), message: z.string() }),
      ]),
    }),
  ),
});

// --- Worklist and samples ------------------------------------------------------------------

export const LAB_WORKLIST_STATES = [
  'to_collect',
  'in_progress',
  'to_validate',
  'released',
] as const;
export type LabWorklistState = (typeof LAB_WORKLIST_STATES)[number];

export const labWorklistQuerySchema = z.object({
  state: z.enum(LAB_WORKLIST_STATES),
  sectionId: z.uuid().optional(),
});

export const labPatientSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  patientNumber: z.string(),
  sex: sexSchema,
  age: ageSchema.nullable(),
});

export const labWorklistRowSchema = z.object({
  orderId: z.uuid(),
  visitId: z.uuid(),
  patient: labPatientSchema,
  testName: z.string(),
  sectionId: z.uuid(),
  sectionName: z.string(),
  priority: z.enum(LAB_PRIORITIES),
  orderedAt: z.iso.datetime(),
  waitingSince: z.iso.datetime(),
  minutesWaiting: z.number().int(),
  overTarget: z.boolean(),
  gate: z.enum(LAB_GATE_STATES),
  /** The SHA approval gate, on an SHA visit still to collect; null otherwise. */
  shaApproval: shaApprovalSchema.nullable(),
  sample: z
    .object({ id: z.uuid(), sampleNumber: z.string(), collectedAt: z.iso.datetime() })
    .nullable(),
  reportStatus: z.enum(LAB_REPORT_STATUSES).nullable(),
  /** A critical result nobody has acknowledged. */
  criticalUnacknowledged: z.boolean(),
});
export type LabWorklistRow = z.infer<typeof labWorklistRowSchema>;

export const labWorklistSchema = z.object({ items: z.array(labWorklistRowSchema) });

export const collectSamplesRequestSchema = z.object({
  clientRequestId: z.uuid(),
  orderIds: z.array(z.uuid()).min(1).max(30),
  /** Clinical time of collection; default now. */
  collectedAt: z.iso.datetime().optional(),
});

export const labSampleSchema = z.object({
  id: z.uuid(),
  sampleNumber: z.string(),
  visitId: z.uuid(),
  patient: labPatientSchema,
  specimenType: codeDisplaySchema,
  container: codeDisplaySchema,
  collectedAt: z.iso.datetime(),
  collectedBy: actorSchema,
  receivedAt: z.iso.datetime().nullable(),
  status: z.enum(['collected', 'received', 'rejected', 'entered_in_error']),
  rejectionReason: z.string().nullable(),
  labelPrintCount: z.number().int(),
  orders: z.array(z.object({ id: z.uuid(), testName: z.string(), shortNames: z.string() })),
});
export type LabSampleView = z.infer<typeof labSampleSchema>;

export const collectSamplesResultSchema = z.object({
  samples: z.array(labSampleSchema),
  labelCopies: z.number().int(),
});

export const findSampleRequestSchema = z.object({ sampleNumber: z.string().trim().min(1).max(32) });
export const labelPrintedRequestSchema = z.object({ copies: z.number().int().min(1).max(5) });
export const rejectSampleRequestSchema = z.object({
  reasonCode: z.string().min(1).max(64),
  note: z.string().trim().max(500).nullable().default(null),
});

// --- Results, validation and reports (plan §5–§7) -------------------------------------------

export const labResultInputSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('numeric'), text: z.string().max(32) }),
  z.object({ kind: z.literal('coded'), optionCode: z.string().max(64) }),
  z.object({ kind: z.literal('text'), text: z.string().max(2000) }),
  z.object({ kind: z.literal('not_done'), reason: z.string().max(500) }),
]);

export const saveLabResultsSchema = z.object({
  expectedRevision: z.number().int().min(1),
  results: z
    .array(
      z.object({
        analyteId: z.uuid(),
        input: labResultInputSchema,
        comment: z.string().trim().max(500).nullable().default(null),
      }),
    )
    .max(60),
  comment: z.string().trim().max(1000).nullable().optional(),
});

/** Why the technologist is sure of a critical value (plan §6.1, C5). */
export const criticalAcknowledgementSchema = z.object({
  analyteId: z.uuid(),
  reason: reasonSchema,
});

export const submitLabReportSchema = z.object({
  expectedRevision: z.number().int().min(1),
  acknowledgements: z.array(criticalAcknowledgementSchema).max(60).default([]),
  /** Required when the culture is marked critical (lab QC and microbiology plan Q10). */
  cultureCriticalReason: reasonSchema.nullable().default(null),
});

export const labRevisionSchema = z.object({ expectedRevision: z.number().int().min(1) });
export const labReasonSchema = z.object({ reason: reasonSchema });
export const labReportPrintedSchema = z.object({ copy: z.number().int().min(1).max(20) });

export const labRangeSchema = z.object({
  /** e.g. "3.5 – 5.1" or "< 5.1"; formatted by the server from the rule in force. */
  text: z.string(),
  provisional: z.boolean(),
});

export const labResultSchema = z.object({
  id: z.uuid(),
  analyteId: z.uuid(),
  analyteName: z.string(),
  resultType: z.enum(LAB_RESULT_TYPES),
  unit: labUnitSchema.nullable(),
  loinc: codedValueSchema.nullable(),
  /** What was entered, for editing: the same shape as the input. */
  input: labResultInputSchema,
  /** As a person reads it: "4.25", "< 0.50", "Positive", "Not done: …". */
  display: z.string(),
  comparator: z.enum(LAB_COMPARATORS).nullable(),
  interpretation: z.enum(LAB_INTERPRETATIONS),
  range: labRangeSchema.nullable(),
  comment: z.string().nullable(),
  enteredBy: actorSchema,
  enteredAt: z.iso.datetime(),
  /** The analyzer's name when the value came from one (lab connector plan §6). */
  fromAnalyzer: z.string().nullable(),
});
export type LabResultView = z.infer<typeof labResultSchema>;

export const labReportSchema = z.object({
  id: z.uuid(),
  orderId: z.uuid(),
  testName: z.string(),
  version: z.number().int(),
  revision: z.number().int(),
  status: z.enum(LAB_REPORT_STATUSES),
  comment: z.string().nullable(),
  correctionReason: z.string().nullable(),
  reportNote: z.string().nullable(),
  components: z.array(labComponentSchema.extend({ options: z.array(labAnalyteOptionSchema) })),
  results: z.array(labResultSchema),
  /** The latest released result for the same analyte for this patient, for comparison. */
  previous: z.array(
    z.object({ analyteId: z.uuid(), display: z.string(), releasedAt: z.iso.datetime() }),
  ),
  submitted: z.object({ by: actorSchema, at: z.iso.datetime() }).nullable(),
  technicallyValidated: z.object({ by: actorSchema, at: z.iso.datetime() }).nullable(),
  released: z.object({ by: actorSchema, at: z.iso.datetime() }).nullable(),
  statusReason: z.string().nullable(),
  /** Every version of this order's report, newest first. */
  versions: z.array(
    z.object({
      id: z.uuid(),
      version: z.number().int(),
      status: z.enum(LAB_REPORT_STATUSES),
      correctionReason: z.string().nullable(),
      releasedAt: z.iso.datetime().nullable(),
    }),
  ),
  workflow: z.enum(LAB_TEST_WORKFLOWS),
  /** Culture tests only: the culture on this report version; null before anything is saved. */
  culture: cultureSchema.nullable(),
  /** Culture tests only: preliminary reports issued from this version, oldest first. */
  preliminaryReports: z.array(preliminaryCultureReportSchema),
  /** QC holds that stop this report being validated (empty once released). */
  qcHolds: z.array(qcHoldSchema),
});
export type LabReportView = z.infer<typeof labReportSchema>;

/** The page the technologist works on: a sample and every order on it. */
export const labSampleWorkSchema = z.object({
  sample: labSampleSchema,
  orderingClinician: z.string().nullable(),
  settings: labSettingsSchema,
  reports: z.array(labReportSchema),
  /**
   * How each analyte on the sample is entered, and the reference range for this patient's
   * age and sex at collection (null: "No range set"), shown beside the field before a
   * value is typed (DESIGN.md §8, lab worklist). The server's judgement on save is the one
   * stored.
   */
  analytes: z.array(
    z.object({
      analyteId: z.uuid(),
      decimals: z.number().int().nullable(),
      allowsNegative: z.boolean(),
      range: labRangeSchema.nullable(),
    }),
  ),
});
export type LabSampleWorkView = z.infer<typeof labSampleWorkSchema>;

/** A released report as the clinician and the printed report see it. */
export const releasedLabReportSchema = labReportSchema.extend({
  sampleNumber: z.string(),
  specimenType: z.string(),
  collectedAt: z.iso.datetime(),
  receivedAt: z.iso.datetime().nullable(),
  orderedAt: z.iso.datetime(),
  requester: z.string().nullable(),
});
export type ReleasedLabReportView = z.infer<typeof releasedLabReportSchema>;

export const patientLabResultsSchema = z.object({
  items: z.array(releasedLabReportSchema),
  /** Culture reports not yet final: the latest preliminary report of each (plan §1.2.9). */
  preliminary: z.array(patientPreliminaryReportSchema),
});

// --- Clinical notifications (plan §6) --------------------------------------------------------

export const CLINICAL_NOTIFICATION_KINDS = [
  'lab_critical_result',
  'lab_report_corrected',
  'lab_report_entered_in_error',
] as const;

export const clinicalNotificationSchema = z.object({
  id: z.uuid(),
  kind: z.enum(CLINICAL_NOTIFICATION_KINDS),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  reportId: z.uuid(),
  orderId: z.uuid(),
  testName: z.string(),
  createdAt: z.iso.datetime(),
  status: z.enum(['open', 'acknowledged', 'closed_by_lab']),
  requiresAcknowledgement: z.boolean(),
  /** Whether I see it because it escalated to me, not because I ordered the test. */
  asEscalation: z.boolean(),
  escalatedAt: z.iso.datetime().nullable(),
  communication: z
    .object({ clinicianName: z.string(), communicatedAt: z.iso.datetime(), by: actorSchema })
    .nullable(),
});
export type ClinicalNotificationView = z.infer<typeof clinicalNotificationSchema>;

export const clinicalNotificationListSchema = z.object({
  items: z.array(clinicalNotificationSchema),
});

export const acknowledgeNotificationSchema = z.object({
  note: z.string().trim().max(500).nullable().default(null),
});

export const recordCommunicationSchema = z.object({
  clinicianName: z.string().trim().min(3).max(120),
  communicatedAt: z.iso.datetime(),
  /** The clinician read the value back. Only a read-back closes the loop for the lab. */
  readBack: z.literal(true),
});

// --- Ranges (plan §2.6): the triage clinical rules model, for analytes ---------------------

export const LAB_RULE_KINDS = ['possible_limits', 'reference_range', 'critical_range'] as const;

export const labRuleSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  analyteId: z.uuid(),
  kind: z.enum(LAB_RULE_KINDS),
  ageFromDays: z.number().int(),
  ageToDays: z.number().int().nullable(),
  sexCode: z.string().nullable(),
  /** In the analyte's unit and decimals, e.g. "3.5". */
  low: z.string().nullable(),
  high: z.string().nullable(),
  source: z.string(),
  approval: z.enum(['provisional', 'approved', 'retired']),
  approvedBy: actorSchema.nullable(),
  approvedAt: z.iso.datetime().nullable(),
  changedBy: actorSchema,
  updatedAt: z.iso.datetime(),
});
export type LabRuleView = z.infer<typeof labRuleSchema>;

export const labRulesSchema = z.object({ analyteId: z.uuid(), rules: z.array(labRuleSchema) });

/**
 * Replaces the live rules of one analyte. Rules sent with an ID are kept (and go back to
 * provisional when their numbers change); rules left out are retired; new ones start
 * provisional. Bounds are typed in the analyte's unit and decimals.
 */
export const putLabRulesSchema = z.object({
  rules: z
    .array(
      z.object({
        id: z.uuid().optional(),
        kind: z.enum(LAB_RULE_KINDS),
        ageFromDays: z.number().int().min(0).max(54_750),
        ageToDays: z.number().int().min(1).max(54_750).nullable(),
        sexCode: z.string().min(1).max(64).nullable(),
        low: z.string().trim().max(32).nullable(),
        high: z.string().trim().max(32).nullable(),
        source: z.string().trim().min(3).max(300),
      }),
    )
    .max(60),
});
